import React from 'react';
import {
  Typography,
  Box,
  Slider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CompactColorPicker from 'ui/components/CompactColorPicker/CompactColorPicker';
import { ShapeElementProps } from '../../../types';

interface ShapePropertiesProps {
  selectedElement: ShapeElementProps;
  updateElementProperty: (
    key: keyof ShapeElementProps,
    value: string | number | boolean
  ) => void;
  lineProps: ShapeElementProps;
  setLineProps: React.Dispatch<React.SetStateAction<ShapeElementProps>>;
}

const ShapeProperties: React.FC<ShapePropertiesProps> = ({
  selectedElement,
  updateElementProperty,
  lineProps,
  setLineProps,
}) => {
  const isTransparent = lineProps.fill === 'transparent';

  const updateProperty = <T extends keyof ShapeElementProps>(
    key: T,
    value: ShapeElementProps[T]
  ) => {
    if (value === undefined) return;
    setLineProps((prev) => ({ ...prev, [key]: value }));

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      updateElementProperty(key, value);
    }
  };

  const handleLineWidthChange = (_: Event, value: number | number[]) => {
    updateProperty('lineWidth', value as number);
  };

  const handleTransparentToggle = (checked: boolean) => {
    updateProperty('fill', checked ? 'transparent' : '#ffffff');
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Style Properties
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          Line Width: {lineProps.lineWidth}px
        </Typography>
        <Slider
          value={lineProps.lineWidth || 1}
          onChange={handleLineWidthChange}
          min={1}
          max={10}
          valueLabelDisplay="auto"
        />
      </Box>
      <CompactColorPicker
        label="Border Color"
        color={lineProps.stroke || '#000000'}
        onChange={(color) => updateProperty('stroke', color)}
      />
      {selectedElement.type === 'box' && (
        <Box sx={{ mt: 1 }}>
          <CompactColorPicker
            label="Fill Color"
            color={isTransparent ? '#ffffff' : lineProps.fill || '#ffffff'}
            onChange={(color) => {
              if (!isTransparent) {
                updateProperty('fill', color);
              }
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isTransparent}
                onChange={(e) => handleTransparentToggle(e.target.checked)}
              />
            }
            label="Transparent"
            sx={{ ml: 'auto' }}
          />
        </Box>
      )}
    </>
  );
};

export default ShapeProperties;
